import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const languages: LanguageOption[] = [
  { code: "en", label: "English", flag: "ri-flag-2-line" },
  { code: "pl", label: "Polski", flag: "ri-flag-2-line" },
  { code: "es", label: "Español", flag: "ri-flag-2-line" },
  { code: "fr", label: "Français", flag: "ri-flag-2-line" },
];

export default function LanguageSwitcher({ variant = "light" }: { variant?: "light" | "dark" }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const changeLanguage = (code: string) => {
    const path = slug ? `/${code}/article/${slug}` : `/${code}`;
    navigate(path);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
          variant === "dark"
            ? "text-background-300 hover:bg-background-800"
            : "text-foreground-700 hover:bg-background-200"
        }`}
        aria-label="Select language"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`${current.flag} text-base`}></i>
        </span>
        <span className="uppercase">{current.code}</span>
        <span className="w-3 h-3 flex items-center justify-center">
          <i className="ri-arrow-down-s-line text-xs"></i>
        </span>
      </button>

      {open && (
        <div
          className={`absolute right-0 mt-2 w-40 rounded-lg border p-1 z-50 ${
            variant === "dark"
              ? "border-background-700 bg-background-900"
              : "border-background-200 bg-background-50"
          }`}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLanguage(lang.code)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                lang.code === current.code
                  ? "bg-accent-100 text-accent-900"
                  : variant === "dark"
                    ? "text-background-200 hover:bg-background-800"
                    : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${lang.flag} text-base`}></i>
              </span>
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}